/* eslint-disable */
import { pathOr } from 'ramda';

import { getInitialPageInfo, authenticate } from '@/api/auth.api';
import { getDysfunctions } from '@/api/dysfunctions.api';
import { getPatientSkinData } from '@/api/patient.api';
import { getPrices } from '@/api/prices.api';
import { getStockInfo } from '@/api/stock.api';
import { getPromoCodeDataOrValidatePromoCode } from '@/api/promo-codes.api';
import {
  getSerumInciList,
  getRecommendedProducts,
  getTreatment,
  getDeliveryData,
  getAvailableDeliveryCountries,
  getEyeContourGenericFormulas,
  setOfferViewed,
  getPrototypeTreatmentDataByCurrentTreatmentId
} from '@/api/treatment.api';
import { getTrustpilotInfo, getTrustpilotReviews } from '@/api/trustpilot.api';
import { NotificationService } from '@/services/NotificationService';
import { log } from '@/utils/loger';
import { getDecodedId } from '@/utils/hashids';

import LocalStorageService from '@/services/LocalStorageService';

import { loadLocale } from '@/i18n';
import {
  CartItem,
  getCartItemName,
  getCartItemQuantity,
  getCartItemUniqueId,
  getCartItemProductPack
} from '@/models/core/CartItem';
import {
  getProductPackGenericEyeSerumFormulaId,
  getProductPackName,
  getProductPackNameByUniqueId,
  getProductPackUniqueId,
  getProductPackUsage,
  isProductPackGenericEyeSerum,
  isProductPackSerum,
  isProductPackInStock,
  isRestrictedSaleInCountry
} from '@/models/core/ProductPack';
import {
  getTreatmentId,
  isRenewedOnlineTreatment,
  getPreviouslyBoughtCartItemsFromRenewedTreatment,
  getTreatmentDtoForSaveCartRequest
} from '@/models/back-end/Treatment';

import { types } from '@/store/types';
import { DELIVERY_TYPES } from '@/constants/shipping';
import { COUNTRY_ISO } from '@/constants/countries';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '@/constants/locale';
import {
  getProductOptionsDefaultOption,
  getProductOptionsProductPacksList
} from '@/models/core/ProductOptions';
import { COMPLEMENTARY_PRODUCT_INCI_LIST, SERUM_USAGES } from '@/constants/treatment';
import { getSerumInciLists } from '@/models/back-end/SerumInciList';
import { getGenericSerumInciLists } from '@/models/back-end/EyeSerumGenericFormulas';
import { UserAlertService } from '@/services/UserAlertService';
import { saveCart } from '@/api/checkout.api';

const STRIPE_PAYMENT_ALREADY_CONFIRMED_ERROR_MESSAGE =
  'You have already CONFIRMED payment for this order';

const isInitialDataStale = (decodedId, cachedData) => {
  if (!cachedData) {
    return true;
  }

  return cachedData.treatmentId !== decodedId;
};

const isTreatmentDataStale = (decodedId, cachedData) => {
  if (!cachedData) {
    return true;
  }

  return cachedData.id !== decodedId;
};

export const actions = {
  async [types.actions.FETCH_INITIAL_PAGE_DATA](
    { commit, dispatch, state },
    { hashedDataId, doctorSlug }
  ) {
    const { [types.state.INITIAL_PAGE_DATA]: cachedInitialData } = state;
    const decodedId = getDecodedId(hashedDataId);

    if (!isInitialDataStale(decodedId, cachedInitialData)) {
      commit(types.mutations.SET_DOCTOR_SLUG, doctorSlug);
      commit(types.mutations.SET_HASHED_DATA_ID, hashedDataId);

      return;
    }

    commit(types.mutations.RESET_STORE);
    commit(types.mutations.SET_DOCTOR_SLUG, doctorSlug);
    commit(types.mutations.SET_HASHED_DATA_ID, hashedDataId);

    const { data } = await getInitialPageInfo(decodedId);

    const {
      treatmentId,
      patientId,
      patientName,
      patientEmail,
      imageId,
      doctorName: doctorFullName,
      doctorEmail,
      doctorOfficePhone: doctorPhone,
      doctorOfficePhoneIso: doctorPhoneIso,
      patientHasPassword: isPatientRegistered,
      landingTextId,
      gender,
      patientLocale,
      treatmentOrigin,
      deliveryCountry,
      hasWebshopAccess
    } = data;

    const initialPageData = {
      treatmentId,
      patientId,
      patientName,
      patientEmail,
      imageId,
      doctorFullName,
      doctorEmail,
      doctorPhone,
      doctorPhoneIso,
      isPatientRegistered: JSON.parse(isPatientRegistered),
      landingTextId,
      gender,
      patientLocale,
      treatmentOrigin,
      deliveryCountry,
      hasWebshopAccess
    };

    commit(types.mutations.SET_INITIAL_PAGE_DATA, initialPageData);
    await dispatch(types.actions.SET_LOCALE, patientLocale);
  },

  async [types.actions.AUTHENTICATE]({ commit, state }) {
    const { [types.state.INITIAL_PAGE_DATA]: initialPageData } = state;

    const { data } = await authenticate({ email: initialPageData.patientEmail });

    commit(types.mutations.SET_TOKEN, data.token);
  },

  async [types.actions.FETCH_WEBSHOP_DATA]({ state, commit, dispatch }, { hashedDataId }) {
    const {
      [types.state.INITIAL_PAGE_DATA]: initialPageData,
      [types.state.TREATMENT_DATA]: cachedTreatmentData
    } = state;

    const { treatmentId } = initialPageData;
    const decodedId = getDecodedId(hashedDataId);

    if (!isTreatmentDataStale(decodedId, cachedTreatmentData)) {
      await dispatch(types.actions.FETCH_UPDATABLE_WEBSHOP_DATA);

      return;
    }

    const [
      { data: patientSkinData },
      { data: dysfunctionsData },
      { data: treatmentData },
      { data: recommendedProductsData }
    ] = await Promise.all([
      getPatientSkinData(treatmentId),
      getDysfunctions(treatmentId),
      getTreatment(treatmentId),
      getRecommendedProducts(treatmentId)
    ]);

    commit(types.mutations.SET_PATIENT_SKIN_DATA, patientSkinData);
    commit(types.mutations.SET_DYSFUNCTIONS_DATA, dysfunctionsData);
    commit(types.mutations.SET_TREATMENT_DATA, treatmentData);
    commit(types.mutations.SET_RECOMMENDED_PRODUCT_NAMES, recommendedProductsData);

    const [{ data: deliveryCountries }] = await Promise.all([
      getAvailableDeliveryCountries(treatmentData),
      dispatch(types.actions.FETCH_UPDATABLE_WEBSHOP_DATA)
    ]);

    commit(types.mutations.SET_AVAILABLE_DELIVERY_COUNTRIES, deliveryCountries);

    // it's not critical waiting for response
    dispatch(types.actions.PREFILL_CART, treatmentData).catch(log.error);
    setOfferViewed(treatmentData).catch(log.error);
  },

  async [types.actions.FETCH_UPDATABLE_WEBSHOP_DATA]({ state, commit, dispatch, getters }) {
    const {
      [types.getters.DOES_TREATMENT_CONTAIN_EYE_SERUM_GENERIC]: isEyeContourSerumGeneric
    } = getters;
    const treatment = state[types.state.TREATMENT_DATA];

    const [{ data: prices }, { data: genericFormulasList }] = await Promise.all([
      getPrices(treatment),
      isEyeContourSerumGeneric
        ? getEyeContourGenericFormulas(treatment)
        : Promise.resolve({ data: [] }),
      dispatch(types.actions.FETCH_STOCK_INFO)
    ]);

    commit(types.mutations.SET_EYE_CONTOUR_SERUM_GENERIC_FORMULAS_LIST, genericFormulasList);
    commit(types.mutations.SET_PRICES, prices);

    // do not wait for the delivery data to download
    dispatch(types.actions.FETCH_DELIVERY_DATA).catch(log.error);
  },

  async [types.actions.FETCH_STOCK_INFO]({ state, commit }) {
    try {
      const treatment = state[types.state.TREATMENT_DATA];
      const { data: stock } = await getStockInfo(treatment);
      commit(types.mutations.SET_STOCK, stock);
      return {};
    } catch (error) {
      return { error: error.message };
    }
  },

  async [types.actions.GET_INCI_LISTS]({ commit, state, getters }, productPack) {
    const productName = getProductPackName(productPack);

    if (!isProductPackSerum(productPack)) {
      return [COMPLEMENTARY_PRODUCT_INCI_LIST[productName]];
    }

    if (isProductPackGenericEyeSerum(productPack)) {
      const {
        [types.state.EYE_CONTOUR_SERUM_GENERIC_FORMULAS_LIST]: eyeSerumGenericFormulasDto
      } = state;

      return getGenericSerumInciLists(productPack, eyeSerumGenericFormulasDto);
    }

    const { [types.state.SERUM_INCI_LISTS]: serumInciLists } = state;
    const {
      [types.getters.DELIVERY_COUNTRY]: deliveryCountry,
      [types.getters.TREATMENT_ID]: treatmentId
    } = getters;

    let serumInciDto = serumInciLists;

    if (!serumInciLists) {
      const { data } = await getSerumInciList(treatmentId, deliveryCountry);
      serumInciDto = data;

      commit(types.mutations.SET_SERUM_INCI_LISTS, serumInciDto);
    }

    const allInciLists = getSerumInciLists(productPack, serumInciDto);
    const packUsage = getProductPackUsage(productPack);

    const twoDifferentBottles = packUsage === SERUM_USAGES.ONE_MORNING_ONE_EVENING;
    const deduplicatedInciLists = twoDifferentBottles ? allInciLists : [allInciLists[0]];

    return deduplicatedInciLists;
  },

  async [types.actions.PUT_PRODUCT_PACK_IN_CART_OR_OPEN_CART](
    { commit, dispatch, getters },
    productPack
  ) {
    const {
      [types.getters.GET_IS_PRODUCT_PACK_ADDED_TO_CART]: getIsProductPackAddedToCart,
      [types.getters.GET_IS_PRODUCT_ADDED_TO_CART_BY_NAME]: getIsProductAddedToCartByName
    } = getters;

    const productName = getProductPackName(productPack);
    const isProductInCart = getIsProductPackAddedToCart(productPack);

    if (isProductInCart) {
      commit(types.mutations.SET_IS_CART_OPEN, true);

      return;
    }

    const isOtherGenericFormulaInCart =
      getIsProductAddedToCartByName(productName) && isProductPackGenericEyeSerum(productPack);

    await dispatch(types.actions.PUT_CART_ITEM_IN_CART, CartItem(productPack, 1));

    if (isOtherGenericFormulaInCart) {
      NotificationService.dispatch(
        NotificationService.NOTIFICATION_NAME.GENERIC_FORMULA_SWITCHED_IN_CART,
        getProductPackGenericEyeSerumFormulaId(productPack)
      );
    } else {
      NotificationService.dispatch(
        NotificationService.NOTIFICATION_NAME.PRODUCT_ADDED_TO_CART,
        productName
      );
    }
  },

  async [types.actions.FETCH_DELIVERY_DATA]({ commit, state, getters }) {
    const {
      [types.state.TREATMENT_DATA]: treatmentData,
      [types.state.EYE_CONTOUR_SERUM_GENERIC_FORMULAS_LIST]: genericFormulasDto
    } = state;
    const {
      [types.getters.DELIVERY_COUNTRY]: deliveryCountry,
      [types.getters.CART_ITEMS]: cartItems
    } = getters;

    if (!treatmentData) {
      throw Error('No treatment data');
    }

    if (deliveryCountry.toLowerCase() === COUNTRY_ISO.MEXICO.toLowerCase()) {
      commit(types.mutations.UPDATE_DELIVERY_TYPE, DELIVERY_TYPES.FIXED);

      return;
    }

    const promoCode = pathOr(null, [types.state.PROMO_CODE_DATA, 'promoCode'], state);
    const treatmentForOrder = getTreatmentDtoForSaveCartRequest(
      cartItems,
      treatmentData,
      genericFormulasDto
    );

    LocalStorageService.saveTestData({ treatment: treatmentForOrder });

    const { data: deliveryData } = await getDeliveryData({
      treatmentData: treatmentForOrder,
      promoCode
    });

    commit(types.mutations.SET_DELIVERY_DATA, deliveryData);

    const { classicDeliveryDays, expressDeliveryDays } = deliveryData || {};

    let deliveryType = DELIVERY_TYPES.FIXED;

    if (classicDeliveryDays) {
      deliveryType = DELIVERY_TYPES.CLASSIC;
    } else if (expressDeliveryDays) {
      deliveryType = DELIVERY_TYPES.EXPRESS;
    }

    commit(types.mutations.UPDATE_DELIVERY_TYPE, deliveryType);
  },

  async [types.actions.SET_LOCALE]({ commit }, locale) {
    const safeLocale = SUPPORTED_LOCALES.includes(locale) ? locale : DEFAULT_LOCALE;
    commit(types.mutations.SET_LOCALE, safeLocale);

    await loadLocale(safeLocale);
  },

  async [types.actions.SET_DELIVERY_COUNTRY]({ commit, state, dispatch }, country) {
    const treatmentData = state[types.state.TREATMENT_DATA];
    const data = { ...treatmentData, customerCountryCode: country.toLowerCase() };

    commit(types.mutations.SET_TREATMENT_DATA, data);

    const isPromoCodeValid = await dispatch(types.actions.ENSURE_PROMO_CODE_VALIDITY);

    if (!isPromoCodeValid) {
      // roll back
      commit(types.mutations.SET_TREATMENT_DATA, treatmentData);

      return { changed: false };
    }

    await dispatch(types.actions.FETCH_UPDATABLE_WEBSHOP_DATA);

    return { changed: true };
  },

  async [types.actions.ENSURE_PROMO_CODE_VALIDITY]({ commit, getters, state }) {
    // return true if promo code is valid after returning from the action
    const {
      [types.getters.DELIVERY_COUNTRY]: deliveryCountry,
      [types.getters.TREATMENT_ID]: treatmentId
    } = getters;
    const { [types.state.PROMO_CODE_DATA]: promoCodeData } = state;

    if (!promoCodeData) {
      // no promo code is considered as valid state
      return true;
    }

    try {
      await getPromoCodeDataOrValidatePromoCode(
        treatmentId,
        promoCodeData.promoCode,
        deliveryCountry
      );

      return true;
    } catch (error) {
      const {
        response: {
          data: { description }
        }
      } = error;

      const userApprovedRemovingPromoCode = await UserAlertService.askUserForAproval(
        UserAlertService.ALERT_TYPE.INVALID_PROMO_CODE_WARNING,
        description
      );

      if (userApprovedRemovingPromoCode) {
        commit(types.mutations.UPDATE_PROMO_CODE_DATA, null);

        return true;
      }

      return false;
    }
  },

  [types.actions.SET_PRODUCT_QUANTITY_IN_CART]({ dispatch }, { productPack, quantity }) {
    const cartItem = CartItem(productPack, quantity);
    const productId = getProductPackUniqueId(productPack);

    if (quantity <= 0) {
      return dispatch(types.actions.REMOVE_CART_ITEM_FROM_CART, productId);
    }

    return dispatch(types.actions.PUT_CART_ITEM_IN_CART, cartItem);
  },

  async [types.actions.PREFILL_CART]({ commit, dispatch, getters }, treatmentData) {
    commit(types.mutations.EMPTY_CART);
    const {
      [types.getters.DELIVERY_COUNTRY]: country
    } = getters;
    if (isRenewedOnlineTreatment(treatmentData)) {
      const { [types.getters.ALL_PRODUCT_OPTIONS_SETS]: allProductOptions } = getters;
      const treatmentId = getTreatmentId(treatmentData);
      const { data: prototypeTreatmentData } = await getPrototypeTreatmentDataByCurrentTreatmentId(
        treatmentId
      );
      
      
      const fullListOfProducts = Object.values(allProductOptions).flatMap(
        getProductOptionsProductPacksList
      );

      const cartItems = getPreviouslyBoughtCartItemsFromRenewedTreatment(
        fullListOfProducts,
        prototypeTreatmentData
      );

      cartItems = cartItems.filter(cartItem=> !isRestrictedSaleInCountry(getCartItemProductPack(cartItem),country));

      await Promise.all(
        cartItems
          .filter(cartItem => isProductPackInStock(getCartItemProductPack(cartItem)) )
          .map(cartItem => dispatch(types.actions.PUT_CART_ITEM_IN_CART, cartItem))
      );
    } else {
      // fill with recommended products
      const {
        [types.getters.RECOMMENDED_PRODUCT_OPTIONS_SETS]: recommendedProductOptions
      } = getters;
      const recommendedProducts = Object.values(recommendedProductOptions).map(
        getProductOptionsDefaultOption
      );

      recommendedProducts.forEach(productPack => {
        if (!isProductPackInStock(productPack)) {
          return;
        }

        if (isRestrictedSaleInCountry(productPack,country)) {
          return;
        }
        dispatch(types.actions.SET_PRODUCT_QUANTITY_IN_CART, { productPack, quantity: 1 });
      });
    }
  },

  [types.actions.GET_TREATMENT_DTO_FOR_SAVE_CART_REQUEST]({ state, getters }) {
    const {
      [types.state.TREATMENT_DATA]: initialTreatmentDto,
      [types.state.EYE_CONTOUR_SERUM_GENERIC_FORMULAS_LIST]: genericFormulasDto
    } = state;
    const { [types.getters.CART_ITEMS]: cartItems } = getters;

    return getTreatmentDtoForSaveCartRequest(cartItems, initialTreatmentDto, genericFormulasDto);
  },

  async [types.actions.LOAD_SITE_DATA]({ dispatch }, { hashedDataId, doctorSlug }) {
    await dispatch(types.actions.FETCH_INITIAL_PAGE_DATA, { hashedDataId, doctorSlug });
    await dispatch(types.actions.AUTHENTICATE);
    await dispatch(types.actions.FETCH_WEBSHOP_DATA, { hashedDataId });
  },

  [types.actions.PUT_CART_ITEM_IN_CART]({ commit, state }, cartItem) {
    const { [types.state.CART]: cart } = state;
    const id = getCartItemUniqueId(cartItem);
    const name = getCartItemName(cartItem);
    const quantity = getCartItemQuantity(cartItem);

    const isAlreadyInCart = cart.some(
      itemDescription => getProductPackNameByUniqueId(itemDescription.id) === name
    );

    let updatedCart;
    if (isAlreadyInCart) {
      updatedCart = cart.map(itemDescription => {
        if (getProductPackNameByUniqueId(itemDescription.id) === name) {
          return { id, quantity };
        }

        return itemDescription;
      });
    } else {
      updatedCart = [...cart, { id, quantity }];
    }

    commit(types.mutations.SET_CART, updatedCart);
  },

  [types.actions.REMOVE_CART_ITEM_FROM_CART]({ state, commit }, productId) {
    const { [types.state.CART]: cart } = state;

    const updatedCart = cart.filter(itemDescription => itemDescription.id !== productId);
    commit(types.mutations.SET_CART, updatedCart);
  },

  async [types.actions.MAKE_ORDER]({ state, dispatch, getters }, paymentInfo) {
    const isPromoCodeValid = await dispatch(types.actions.ENSURE_PROMO_CODE_VALIDITY);

    if (!isPromoCodeValid) {
      return { success: false };
    }

    const { [types.getters.TREATMENT_ID]: treatmentId } = getters;
    const { [types.state.PROMO_CODE_DATA]: promoCodeData } = state;
    const saveCartDto = {
      ...paymentInfo,
      promoCode: pathOr(null, ['promoCode'], promoCodeData)
    };

    try {
      const { data } = await saveCart(treatmentId, saveCartDto);

      return { success: true, payload: data };
    } catch (error) {
      const errorMessage = pathOr('', ['response', 'data', 'description'], error);

      if (errorMessage === STRIPE_PAYMENT_ALREADY_CONFIRMED_ERROR_MESSAGE) {
        return { success: true };
      }

      throw error;
    }
  },

  async [types.actions.GET_TRUSTPILOT_DATA]({ commit }) {
    const { data: trustpilotData } = await getTrustpilotInfo();

    commit(types.mutations.SET_TRUSTPILOT_DATA, trustpilotData);
  },

  async [types.actions.GET_TRUSTPILOT_REVIEWS]({ state, commit }) {
    const { [types.state.LOCALE]: locale } = state;

    const { data: reviews } = await getTrustpilotReviews(locale);

    commit(types.mutations.SET_TRUSTPILOT_REVIEWS, reviews);
  }
};
